import { useState } from 'react'
import { Bell, Loader2, RefreshCw, ChevronDown, ChevronUp, Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useReminders } from '@/hooks/useApi'
import { timeAgo } from '@/lib/utils'

const PRIORITY_COLOR: Record<string, string> = {
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  normal: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  low: 'bg-text-muted/20 text-text-muted border-text-muted/30',
}

export function RemindersWidget() {
  const [collapsed, setCollapsed] = useState(false)
  const { data, isLoading, refetch } = useReminders()

  const reminders = data?.reminders ?? []
  const count = reminders.length

  if (count === 0 && !isLoading) return null

  return (
    <div className="mx-4 mb-2">
      <div
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-surface-elevated cursor-pointer hover:border-accent/30 transition-colors"
      >
        <Bell className="h-3.5 w-3.5 text-amber-400" />
        <span className="text-xs font-medium text-text-primary">Reminders</span>
        {count > 0 && <Badge variant="outline" className="text-[10px]">{count}</Badge>}
        {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); refetch(); }}
            className="p-0.5 text-text-muted hover:text-text-primary transition-colors"
          >
            <RefreshCw className="h-3 w-3" />
          </button>
          {collapsed ? <ChevronDown className="h-3 w-3 text-text-muted" /> : <ChevronUp className="h-3 w-3 text-text-muted" />}
        </div>
      </div>

      {!collapsed && count > 0 && (
        <div className="mt-1 space-y-1">
          {reminders.map((rem) => (
            <div
              key={rem.reminder_id}
              className="flex items-start gap-2 px-3 py-2 rounded-lg border border-border/50 bg-surface-elevated/50"
            >
              <Clock className="h-3 w-3 mt-0.5 shrink-0 text-text-muted" />
              <div className="flex-1 min-w-0 space-y-0.5">
                <p className="text-xs text-text-primary">{rem.content}</p>
                <div className="flex items-center gap-2 text-[10px] text-text-muted">
                  <Badge className={`text-[10px] border ${PRIORITY_COLOR[rem.priority] || PRIORITY_COLOR.normal}`}>
                    {rem.priority}
                  </Badge>
                  <span>{timeAgo(new Date(rem.created_at))}</span>
                  {rem.recall_at && <span className="font-mono">{rem.recall_at}</span>}
                </div>
              </div>
            </div>
          ))}
          <p className="text-[10px] text-text-muted px-3 py-1">Dismiss reminders via Ops Bridge chat</p>
        </div>
      )}
    </div>
  )
}
